import { useCallback, useEffect, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add as AddIcon } from '@factorialco/f0-react/icons/app'

import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'
import useClickOutside from 'modules/workflows/hooks/useClickOutside'

import { FlowNodeAddButtonDropdown } from './FlowNodeAddButtonDropdown'
import { NodeUIType } from './types'
import { createNodesAndEdges } from './utils'

type Props = { nodeId: string; nodeType: NodeUIType; isOpen?: boolean }

export const FlowNodeAddButton = ({ nodeId, nodeType, isOpen = false }: Props) => {
  const [open, setOpen] = useState(isOpen)
  const menuRef = useRef<HTMLDivElement>(null)
  const {
    buildingBlocks,
    nodesUI,
    setNodesUI,
    edgesUI,
    setEdgesUI,
    elevateNodeZIndex,
    restoreNodeZIndex,
    menuControls,
  } = useWorkflowContext()

  const closeMenu = useCallback(() => {
    setOpen(false)
    restoreNodeZIndex(nodeId)
  }, [restoreNodeZIndex, nodeId])

  useEffect(() => {
    if (open) {
      return menuControls.registerMenu(nodeId, closeMenu)
    }
  }, [open, nodeId, menuControls, closeMenu])

  useClickOutside(menuRef, () => {
    menuControls.closeAllMenus()
  })

  const handleAddIconClick = (event: React.MouseEvent | MouseEvent) => {
    event.stopPropagation()
    menuControls.closeAllMenus()
    elevateNodeZIndex(nodeId)
    setOpen(true)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      menuControls.closeAllMenus()
      elevateNodeZIndex(nodeId)
      setOpen(true)
    }
  }

  const handleNodeChosenClick = (type: NodeUIType) => {
    const currentNodes = nodesUI
    const currentEdges = edgesUI
    const newNodeId = crypto.randomUUID()

    // Find the current node to get its position
    const currentNode = currentNodes.find((node) => node.id === nodeId)
    if (!currentNode) return

    // Create the new node(s) and edges
    const { nodes: newNodes, edges: newEdges = [] } = createNodesAndEdges(newNodeId, type, {
      x: currentNode.position.x,
      y: currentNode.position.y,
    })

    // For condition nodes, connect all nodes under the current one to the else node
    let updatedEdges = currentEdges
    let connectionEdge: { id: string; source: string; target: string; type: 'default' } | undefined

    if (type === 'condition') {
      // Find the else node (it should be the last node in the array for condition type nodes)
      const elseNode = newNodes.find((node) => node.type === 'conditionElse')
      if (elseNode) {
        // Update outgoing edges to point to the else node instead
        updatedEdges = currentEdges.map((edge) => {
          if (edge.source === nodeId) {
            return { ...edge, source: elseNode.id }
          }
          return edge
        })
        // Create new edge from current node to the main condition node
        connectionEdge = {
          id: `${nodeId}-${newNodeId}`,
          source: nodeId,
          target: newNodeId,
          type: 'default',
        }
      }
    } else {
      // For single nodes, use the existing logic
      const newNode = newNodes[0]
      if (!newNode) return

      // Update outgoing edges to point to the new node instead
      updatedEdges = currentEdges.map((edge) => {
        if (edge.source === nodeId) {
          return { ...edge, source: newNode.id }
        }
        return edge
      })

      // Create new dfe drom current node to new node
      connectionEdge = {
        id: `${nodeId}-${newNode.id}`,
        source: nodeId,
        target: newNode.id,
        type: 'default',
      }
    }

    const finalEdges = connectionEdge
      ? [...updatedEdges, ...newEdges, connectionEdge]
      : [...updatedEdges, ...newEdges]
    setNodesUI([...currentNodes, ...newNodes])
    setEdgesUI(finalEdges)
    closeMenu()
  }

  if (nodeType === 'condition' || nodeType === 'terminate') return null

  return (
    <Box
      position="fixed"
      width="full"
      style={{ left: '0', bottom: '-35px' }}
      flexDirection="column"
      alignItems="center"
    >
      <Box
        position="relative"
        width="full"
        flexDirection="column"
        alignItems="center"
        ref={menuRef}
      >
        <F0Button
          icon={AddIcon}
          hideLabel
          variant="outline"
          size="sm"
          onClick={handleAddIconClick}
          onKeyDown={handleKeyDown}
          aria-label="Add node"
          aria-expanded={open}
          aria-haspopup="menu"
          aria-controls={open ? `building-blocks-menu-${nodeId}` : undefined}
        />

        <FlowNodeAddButtonDropdown
          nodeId={nodeId}
          open={open}
          buildingBlocks={buildingBlocks}
          onNodeChosen={handleNodeChosenClick}
        />
      </Box>
    </Box>
  )
}
